<template>
  <div class="document-form__container">
    <FormTitle title="Заповніть один з документів страхувальника" />

    <div class="document-form__fields">
      <div class="document-form__field-row">
        <DocumentsList
          class="document-form__field"
          placeholder="Виберіть тип документа *"
          :allowed-document-types="allowedDocumentTypes"
          v-model="document.type"
          :error-messages="getValidationErrors('document.type', {
            required: 'Оберіть тип документа',
          })"
          @change="$emit('changeDocumentType', $event)"
          @blur="touchField('document.type')"
        />
      </div>

      <div class="document-form__field-row">
        <div class="document-form__text-field-wrapper">
          <TextInput
            class="document-form__field"
            :placeholder="seriesPlaceholder + ' *'"
            :limit="getSeriesLimit"
            :mask="getSeriesMask"
            v-model="document.series"
            :error-messages="getValidationErrors('document.series', {
              required: 'Введіть серію документа',
              validSeries: seriesErrorMessage,
            })"
            @change="$emit('changeDocumentSeries', $event)"
            @blur="touchField('document.series')"
          />
          <HelpTooltip
            class="document-form__document-tooltip"
            :image="getDocumentSeriesTooltipImage(getDocumentId)"
            image-alt="Серія документа"
          />
        </div>
        <div class="document-form__text-field-wrapper">
          <TextInput
            class="document-form__field"
            :placeholder="isIssuingAuthorityRequired ? 'Ким виданий *' : 'Ким виданий'"
            :limit="100"
            v-model="document.issuedBy"
            :error-messages="getValidationErrors('document.issuedBy', {
              required: 'Введіть орган, що видав документ',
              validIssuingAuthority: 'Введіть коректно дані',
            })"
            @change="$emit('changeDocumentIssuedBy', $event)"
            @blur="touchField('document.issuedBy')"
          />
          <HelpTooltip
            class="document-form__document-tooltip"
            :image="getDocumentIssuedByTooltipImage(getDocumentId)"
            image-alt="Ким виданий документ"
          />
        </div>
      </div>

      <div class="document-form__field-row">
        <div class="document-form__text-field-wrapper">
          <TextInput
            class="document-form__field"
            placeholder="Номер *"
            :limit="getNumberLimit"
            :mask="getNumberMask"
            v-model="document.number"
            :error-messages="getValidationErrors('document.number', {
              required: 'Введіть номер документа',
              validNumber: numberErrorMessage,
            })"
            @change="$emit('changeDocumentNumber', $event)"
            @blur="touchField('document.number')"
          />
          <HelpTooltip
            class="document-form__document-tooltip"
            :image="getDocumentNumberTooltipImage(getDocumentId)"
            image-alt="Номер документа"
          />
        </div>
      </div>

      <div class="document-form__field-row">
        <div class="document-form__text-field-wrapper">
          <DateInput
            class="document-form__field"
            :placeholder="isIssueDateRequired ? 'Дата видачі *' : 'Дата видачі'"
            format="DD.MM.YYYY"
            type="input"
            :disabled-future-dates="true"
            v-model="document.issueDate"
            :error-messages="getValidationErrors('document.issueDate', {
              required: 'Введіть дату видачі документа',
              validIssueDate: issueDateErrorMessage,
            })"
            @change="$emit('changeDocumentIssueDate', $event)"
            @blur="touchField('document.issueDate')"
          />
          <HelpTooltip
            class="document-form__document-tooltip document-form__document-issue-date-tooltip"
            :image="getDocumentIssueDateTooltipImage(getDocumentId)"
            image-alt="Дата видачі документа"
          />
        </div>
        <div v-if="isExpiryDateVisible" class="document-form__text-field-wrapper">
          <DateInput
            class="document-form__field"
            :placeholder="isERetireeDocument ? 'Дата закінчення терміну дії *' : 'Дата закінчення терміну дії'"
            format="DD.MM.YYYY"
            type="input"
            v-model="document.expirationDate"
            :error-messages="getValidationErrors('document.expirationDate', {
              required: 'Введіть дату закінчення терміну дії',
              validExpiryDate: 'Дата закінчення терміну дії має бути сьогодні або пізніше',
            })"
            @change="$emit('changeDocumentExpirationDate', $event)"
            @blur="touchField('document.expirationDate')"
          />
          <HelpTooltip
            class="document-form__document-tooltip document-form__document-issue-date-tooltip"
            :image="getDocumentIssueEndDateTooltipImage(getDocumentId)"
            image-alt="Дата закінчення терміну дії документа"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators';
import { DocumentsList, documentsWithExpiry, DocumentTooltip, eRetireeDocumentIds } from '@/features/selectors';
import { ValidateForm } from '@/shared/lib/form';
import { CompanyLogic, DocumentLogic, DocumentMask, DocumentValidation } from '@/shared/lib/insurance';
import { DateInput, FormTitle, HelpTooltip, TextInput } from '@/shared/ui/form';

const tooltipResult = DocumentTooltip();
const validationResult = DocumentValidation();
const {
  getDocumentNumberTooltipImage,
  getDocumentSeriesTooltipImage,
  getDocumentIssueDateTooltipImage,
  getDocumentIssueEndDateTooltipImage,
  getDocumentIssuedByTooltipImage,
} = tooltipResult;
const { validSeries, validNumber, validIssueDate, validExpiryDate, validIssuingAuthority } = validationResult;
const { seriesMask, seriesLimit, numberMask, numberLimit } = DocumentMask();

export default {
  props: {
    policyholderBirthday: {
      type: [String, Date],
      default: null,
    },
    allowedDocumentTypes: {
      type: Array,
      default: () => null,
    },
    documentType: {
      type: [String, Number],
      default: null,
    },
    documentSeries: {
      type: String,
      default: null,
    },
    documentNumber: {
      type: [String, Number],
      default: null,
    },
    documentIssueDate: {
      type: [String, Date],
      default: null,
    },
    documentIssuedBy: {
      type: String,
      default: null,
    },
    documentExpirationDate: {
      type: [String, Date],
      default: null,
    },
    offerModuleId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      document: {
        type: this.documentType,
        series: this.documentSeries,
        number: this.documentNumber,
        issueDate: this.documentIssueDate,
        issuedBy: this.documentIssuedBy,
        expirationDate: this.documentExpirationDate,
      },
    };
  },
  validations() {
    return {
      document: {
        type: {
          required,
        },
        series: {
          required,
          validSeries: validSeries(this.getDocumentId),
        },
        number: {
          required,
          validNumber: validNumber(this.getDocumentId),
        },
        issueDate: {
          required: requiredIf(() => this.isIssueDateRequired),
          validIssueDate: validIssueDate(this.getDocumentId, this.policyholderBirthday),
        },
        issuedBy: {
          required: requiredIf(() => this.isIssuingAuthorityRequired),
          validIssuingAuthority,
        },
        expirationDate: {
          required: requiredIf(() => this.isERetireeDocument),
          validExpiryDate: validExpiryDate(this.getDocumentId, this.document.issueDate, this.isERetireeDocument),
        },
      },
    };
  },
  mixins: [ValidateForm, CompanyLogic, DocumentLogic],
  components: { HelpTooltip, FormTitle, DocumentsList, TextInput, DateInput },
  computed: {
    getDocumentId() {
      if (!this.document.type) return this.documentType || null;
      if (typeof this.document.type === 'object') return this.document.type.id || null;
      if (typeof this.document.type === 'number') return this.convertDocumentUidToId(this.document.type) || this.document.type || null;
      return this.document.type;
    },
    seriesPlaceholder() {
      if (this.getDocumentId === 'DOCUMENT_ID_CARD' || this.getDocumentId === 'DOCUMENT_ID_RESIDENCE_PERMIT') {
        return 'Запис';
      }
      return 'Серія';
    },
    seriesErrorMessage() {
      if (this.getDocumentId === 'DOCUMENT_ID_CARD' || this.getDocumentId === 'DOCUMENT_ID_RESIDENCE_PERMIT') {
        return 'Введіть коректний запис документа (8 цифр-5 цифр)';
      }
      if (this.getDocumentId === 'DOCUMENT_PASSPORT') {
        return 'Серія паспорта повинна складатися з 2 літер';
      }
      return 'Невірний формат серії';
    },
    numberErrorMessage() {
      if (this.getDocumentId === 'DOCUMENT_PASSPORT') {
        return 'Номер паспорта повинен складатися з 6 цифр';
      }
      if (this.getDocumentId === 'DOCUMENT_ID_CARD') {
        return 'Номер id-картки повинен складатися з 9 цифр';
      }
      if (this.getDocumentId === 'DOCUMENT_DRIVERS_LICENSE') {
        return 'Номер водійського посвідчення повинен складатися з 6 цифр';
      }
      return 'Невірний формат номера';
    },
    issueDateErrorMessage() {
      if (this.getDocumentId === 'DOCUMENT_ID_CARD') {
        return 'На момент видачі документа вам має бути більше 14 років';
      }
      return 'На момент видачі документа вам має бути більше 16 років';
    },
    isEwaCompany() {
      return this.isEwa(this.offerModuleId);
    },
    isDocumentPassport() {
      return this.getDocumentId === 'DOCUMENT_PASSPORT';
    },
    getSeriesMask() {
      return seriesMask(this.getDocumentId);
    },
    getSeriesLimit() {
      return seriesLimit(this.getDocumentId);
    },
    getNumberMask() {
      return numberMask(this.getDocumentId);
    },
    getNumberLimit() {
      return numberLimit(this.getDocumentId);
    },
    isIssueDateRequired() {
      if (this.isEwaCompany && this.isDocumentPassport) return false;
      return true;
    },
    isIssuingAuthorityRequired() {
      if (this.isEwaCompany && this.isDocumentPassport) return false;
      return true;
    },
    isExpiryDateVisible() {
      return documentsWithExpiry.includes(this.getDocumentId) || this.isERetireeDocument;
    },
    isERetireeDocument() {
      return eRetireeDocumentIds.includes(this.getDocumentId);
    },
  },
  methods: {
    getDocumentNumberTooltipImage,
    getDocumentSeriesTooltipImage,
    getDocumentIssueDateTooltipImage,
    getDocumentIssueEndDateTooltipImage,
    getDocumentIssuedByTooltipImage,
  },
  watch: {
    documentType(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.document.type = newValue;
      }
    },
    documentSeries(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.document.series = newValue;
      }
    },
    documentNumber(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.document.number = newValue;
      }
    },
    documentIssueDate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.document.issueDate = newValue;
      }
    },
    documentIssuedBy(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.document.issuedBy = newValue;
      }
    },
    documentExpirationDate(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.document.expirationDate = newValue;
      }
    },
    'document.type'(newType, oldType) {
      if (!newType || newType === oldType) return;

      const newTypeId = typeof newType === 'object' ? newType.id : newType;

      if (eRetireeDocumentIds.includes(newTypeId)) {
        this.document.series = '\u0410\u0410';
        this.document.issuedBy = null;
        this.document.issueDate = null;
        this.$emit('changeDocumentSeries', this.document.series);
        this.$emit('changeDocumentIssuedBy', null);
        this.$emit('changeDocumentIssueDate', null);
      }

      if (!documentsWithExpiry.includes(newTypeId) && !eRetireeDocumentIds.includes(newTypeId)) {
        this.document.expirationDate = null;
        this.$emit('changeDocumentExpirationDate', null);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.document-form {
  &__container {
    margin-bottom: 24px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__field-row {
    display: flex;
    gap: 24px;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
    }
  }

  &__field,
  &__text-field-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;

    @media (max-width: 768px) {
      flex: 1 1 100%;
    }
  }

  &__document-tooltip.help-tooltip {
    position: absolute;
    top: 12px;
    right: 8px;
  }

  &__document-issue-date-tooltip.help-tooltip {
    right: 32px;
  }
}
</style>
